import { SwapOutlined } from '@ant-design/icons';
import { approvalDrawers, approvalModals } from '@sharefiledev/review-approval-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { ReviewApprovalHttp } from './api/reviewApprovalHttp';
import {
	AsyncCreateNewVersionModalExtensionSlot,
	AsyncCreateReviewApprovalDrawerExtensionSlot,
	AsyncCreateReviewApprovalFromStoragePiletDrawerExtensionSlot,
	AsyncExistingReviewApprovalSPDrawerExtensionSlot,
	LazyDevPage,
} from './ExtensionSlots';
import { polyfillPromiseWithResolvers } from './Polyfills/promiseWithResolver';

polyfillPromiseWithResolvers();

const smokeTestCustomers = [
	'pilet-tests.sharefiletest.com',
	'pilet-tests.sharefilestaging.com',
	'pilet-tests.sharefile.com',
];

export function setup(app: PiletApi) {
	/**
	 * Drawer referenced from a file list view with the button saying "Send for approval"
	 */
	app.registerDrawer(
		approvalDrawers.sendForApprovalDrawer,
		props => {
			return (
				<AsyncCreateReviewApprovalDrawerExtensionSlot
					{...props}
					file={props.options?.file}
					containerRid={props.options?.parentContainerRid}
					piletApi={app}
				/>
			);
		},
		{
			drawerProps: {
				title: 'Create Approval Request',
				width: '100%',
				styles: {
					body: {
						padding: '8px',
					},
				},
			},
		}
	);

	/**
	 * Drawer referenced from solution view to create a new request. Also used
	 * in devpage
	 */
	app.registerDrawer(
		approvalDrawers.createEmptyReviewApproval,
		props => {
			return (
				<AsyncCreateReviewApprovalFromStoragePiletDrawerExtensionSlot
					{...props}
					containerRid={props.options?.params.containerRID}
					piletApi={app}
				/>
			);
		},
		{
			drawerProps: {
				title: 'Create Approval Request',
				width: '100%',
			},
		}
	);

	app.registerDrawer(
		approvalDrawers.taskAggregatorSPView,
		props => {
			return (
				<AsyncExistingReviewApprovalSPDrawerExtensionSlot
					{...props}
					containerRid={props.options?.containerRID}
					piletApi={app}
				/>
			);
		},
		{
			drawerProps: {
				title: 'Review Approval Request',
				width: '100%',
				styles: {
					body: {
						padding: '8px',
					},
				},
				push: false,
			},
		}
	);

	app.registerDrawer(
		approvalDrawers.taskAggregatorClientView,
		props => {
			return (
				<AsyncExistingReviewApprovalSPDrawerExtensionSlot
					{...props}
					containerRid={props.options?.params.containerRID}
					piletApi={app}
				/>
			);
		},
		{
			drawerProps: {
				title: 'Review Approval Request',
				width: '100%',
				styles: {
					body: {
						padding: '8px',
					},
				},
			},
		}
	);

	app.registerModal(
		approvalModals.createNewVersion,
		props => {
			return (
				<AsyncCreateNewVersionModalExtensionSlot
					{...props}
					containerRid={props.options?.containerRID!}
					approverIds={props.options?.approverIds}
					submitNewVersion={props.options?.submitNewVersion!}
					piletApi={app}
				/>
			);
		},
		{
			modalProps: {
				width: '80%',
				height: '80%',
				title: 'Create New Version',
			},
		}
	);

	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'reviewapproval',
		defaultPath: '/reviewapprovals/{RID}',
		clientPath: '/',
		callback: async (rid: string) => {
			const parsed = RID.parse(rid);
			const rapRID = parsed.findEntityType('reviewapproval');

			if (rapRID == null) {
				return Promise.reject('Invalid RID');
			}

			return await ReviewApprovalHttp.getV1Approval(rapRID.toString());
		},
	});

	if (
		process.env.NODE_ENV === 'development' ||
		smokeTestCustomers.includes(location.hostname)
	) {
		app.sf.registerLeftNavComponent({
			href: '/rap-dev',
			title: () => 'RAP Dev',
			icon: () => <SwapOutlined style={{ fontSize: '24px' }} />,
			weight: 50,
		});

		app.registerPage('/rap-dev', () => <LazyDevPage piletApi={app} />);
	}
}
